import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  actionFetchUpdatedIdCard,
  actionSetPageUpdatedIDCard,
} from "../../actions/UpdatedIdCards.action";
import SnackbarUtils from "../../libs/SnackbarUtils";
import {
  serviceDownloadUpdatedEmpsIdCards,
} from "../../services/UpdatedIdCard.service";

const getDefaultDates = () => {
  const today = new Date();

  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

  return {
    start_date: firstDay,
    end_date: today,
  };
};

const formatDateToAPI = (date) => {
  if (!date) return "";
  const d = new Date(date);

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const initialForm = {
  ...getDefaultDates(),
};
const useUpdatedIdCardsHook = () => {
  const [form, setForm] = useState(initialForm);
  const [errorData, setErrorData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCard, setSelectedCard] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  
  const { user } = useSelector((state) => state.auth);
  const [listData, setListData] = useState({
    LOCATIONS: [],
    DEPARTMENTS: [],
    FY_YEAR: [],
  });
  const dispatch = useDispatch();
  const isMountRef = useRef(false);

  const {
    sorting_data: sortingData,
    is_fetching: isFetching,
    query,
    query_data: queryData,
  } = useSelector((state) => state.updated_id_card);



const resetData = useCallback(
  (sort = {}, filters = {}) => {
      dispatch(
        actionFetchUpdatedIdCard(
          1,
          { sortingData, ...sort },
          {
            query: filters?.query ?? (isMountRef.current ? query : null),
            query_data: filters?.query_data ?? (isMountRef.current ? queryData : null),
            start_date:formatDateToAPI(form?.start_date),
            end_date:formatDateToAPI(form?.end_date)
          }
        )
      );
      isMountRef.current = true;
    },
    [query, queryData, form, sortingData]
  );

  useEffect(() => {
    resetData();
  }, [form]);

  const removeError = useCallback(
    (title) => {
      const temp = JSON.parse(JSON.stringify(errorData));
      temp[title] = false;
      setErrorData(temp);
    },
    [setErrorData, errorData]
  );

  const changeTextData = useCallback(
    (text, fieldName) => {
      let shouldRemoveError = true;
      const t = { ...form };

      t[fieldName] = text;

      const start = new Date(
        fieldName === "start_date" ? text : t.start_date
      );
      const end = new Date(
        fieldName === "end_date" ? text : t.end_date
      );

      if (start > end) {
        SnackbarUtils.error("Start date cannot be greater than end date");
        return;
      }

      setForm(t);

      shouldRemoveError && removeError(fieldName);
    },
    [removeError, form, setForm]
  );

  const formatDateToDisplay = (date) => {
    if (!date) return "-";

    const d = new Date(date);

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const checkFormValidation = useCallback(() => {
    const errors = { ...errorData };
    let required = ["start_date", "end_date"];

    required.forEach((val) => {
      if (
        !form?.[val] ||
        (Array.isArray(form?.[val]) && form?.[val].length === 0)
      ) {
        errors[val] = true;
      }
    });
    Object.keys(errors).forEach((key) => {
      if (!errors[key]) {
        delete errors[key];
      }
    });
    return errors;
  }, [form, errorData]);

  const handlePageChange = useCallback((type) => {
    console.log("_handlePageChange", type);
    dispatch(actionSetPageUpdatedIDCard(type, form));
  }, [form]);

  const queryFilter = useCallback(
    (key, value) => {
      resetData(
        {},
        {
          query: key === "SEARCH_TEXT" ? value : query,
          query_data: key === "FILTER_DATA" ? value : queryData,
        }
      );
    },
    [query, queryData, resetData]
  );

  const handleFilterDataChange = useCallback(
    (value) => {
      queryFilter("FILTER_DATA", value);
    },
    [queryFilter]
  );

  const handleSearchValueChange = useCallback(
    (value) => {
      queryFilter("SEARCH_TEXT", value);
    },
    [queryFilter]
  );

  const handleSortOrderChange = useCallback(
    (row, order) => {
      dispatch(actionSetPageUpdatedIDCard(1, form));
      resetData({ row, order }, {});
    },
    [dispatch, form, resetData]
  );

  const handleRowSize = (page) => {
    console.log(page);
  };

  const handleToggle = useCallback(
    () => {
      setOpenPreview((e) => !e);
    },
    []
  );

  const handleConfirmToggle = useCallback(
    (data) => {
      setSelectedCard((e) => !e);
    },
    [setSelectedCard]
  );

  const handleDownload = useCallback(async () => {
    try {
      if (!isSubmitting) {
        setIsLoading(true);

        const res = await serviceDownloadUpdatedEmpsIdCards({
          start_date: formatDateToAPI(form.start_date),
          end_date: formatDateToAPI(form.end_date),
        }, user?.token);

        if (!res.error) {
          const blob = await res.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Employee_ID_Cards.csv");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          SnackbarUtils.error(res?.message);
        }

        setIsLoading(false);
      }
    } catch (error) {
      console.error(error);
      SnackbarUtils.error("Download failed");
      setIsLoading(false);
    }
  }, [isLoading, form]);

  const configFilter = useMemo(() => {
    return [
      {
      },
    ];
  }, [listData]);

  const handleView = (fileUrl) => {
    if (fileUrl) {
      window.open(fileUrl, "_blank");
    }
  };

  const handleIdDownload = async (fileUrl) => {
    try {
      if (!fileUrl) return;

      const response = await fetch(fileUrl, {
        method: "GET",
      });

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "ID_Card.pdf";

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      SnackbarUtils.error("PDF download failed");
    }
  };

  return {
    handlePageChange,
    handleFilterDataChange,
    handleSearchValueChange,
    handleRowSize,
    handleSortOrderChange,
    handleToggle,
    openPreview,
    configFilter,
    handleDownload,
    listData,
    form,
    handleView,
    handleIdDownload,
    errorData,
    changeTextData,
    isSubmitting,
    resetData,
    handleConfirmToggle,
    isLoading,
    selectedCard,
    formatDateToDisplay,
  };
};

export default useUpdatedIdCardsHook;
